/**
 * MuscleHighlighter Component
 * 
 * Reusable component for displaying muscle highlighting on an anatomy diagram.
 * Extracted from TargetMuscle.js for use across the application.
 * 
 * Features:
 * - SVG-based anatomy diagram with bilateral muscle groups
 * - Dynamic muscle highlighting (primary/secondary)
 * - Hover tooltips for muscle identification
 * - Theme-aware styling
 * - Responsive design
 */
import React, { useState, useEffect, useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactComponent as MuscleGroupSvg } from '../assets/muscle_outlined_grouped_compressed.svg';
import { getBilateralMuscles, formatMuscleName } from '../utils/muscleUtils';
import { 
  createMuscleToExerciseMapping, 
  getExercisesForMuscle, 
  formatExerciseTooltip, 
  mapSvgMuscleToExerciseMuscle 
} from '../utils/exerciseMuscleMapping';

// Using shared MuscleGroupSvg component from assets

const Tooltip = styled(Box)(({ theme }) => ({
  position: 'fixed',
  display: 'none',
  padding: '8px 12px',
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  color: 'white',
  borderRadius: '6px',
  fontSize: '14px',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
  zIndex: 1000,
}));

const MuscleHighlighter = ({ 
  primaryMuscles = [], 
  secondaryMuscles = [], 
  showTooltips = true,
  size = 'medium', // small, medium, large
  exercises = [] // Array of exercise objects for tooltip information
}) => {
  const theme = useTheme();
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipContent, setTooltipContent] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef(null);
  
  // Create muscle to exercise mapping when exercises prop changes
  const muscleToExerciseMap = React.useMemo(() => {
    return createMuscleToExerciseMapping(exercises);
  }, [exercises]);

  // Using shared utility functions from muscleUtils

  const resetMuscles = () => {
    const allMuscles = document.querySelectorAll('.muscle-group');
    allMuscles.forEach(muscle => {
      muscle.classList.remove('highlighted', 'secondary-muscle');
      // Clear inline styles
      muscle.style.fill = '';
      muscle.style.stroke = '';
      muscle.style.strokeWidth = '';
      muscle.style.filter = '';
      muscle.style.animation = '';
    });
  };

  const highlightMuscles = () => {
    resetMuscles();
    
    // Get bilateral muscles for primary targets
    const expandedPrimary = getBilateralMuscles(primaryMuscles);
    expandedPrimary.forEach(muscleId => {
      const element = document.getElementById(muscleId);
      if (element) {
        element.classList.add('highlighted');
        // Force style application with aesthetic colors
        element.style.fill = '#6366f1';
        element.style.stroke = '#4f46e5';
        element.style.strokeWidth = '3px';
        element.style.filter = 'drop-shadow(0 0 6px rgba(99, 102, 241, 0.4))';
        element.style.animation = 'pulse 2s infinite';
      }
    });
    
    // Get bilateral muscles for secondary targets
    const expandedSecondary = getBilateralMuscles(secondaryMuscles);
    expandedSecondary.forEach(muscleId => {
      const element = document.getElementById(muscleId);
      if (element) {
        element.classList.add('secondary-muscle');
        // Force style application with aesthetic colors
        element.style.fill = '#f59e0b';
        element.style.stroke = '#d97706';
        element.style.strokeWidth = '2px';
        element.style.filter = 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.3))';
      }
    });
  };

  const handleMouseOver = (event) => {
    if (!showTooltips) return;
    
    const target = event.target;
    // Check if the target is a path or g element with an ID (muscle group)
    if (target.tagName === 'path' || target.tagName === 'g') {
      const muscleId = target.id;
      if (muscleId && muscleId !== 'full_body_back_outliner' && muscleId !== 'full_body_front_outliner') {
        const muscleName = formatMuscleName(muscleId);
        
        // Map SVG muscle ID to exercise muscle name and get exercises
        const exerciseMuscleName = mapSvgMuscleToExerciseMuscle(muscleId);
        const exercisesForMuscle = getExercisesForMuscle(exerciseMuscleName, muscleToExerciseMap);
        
        // Format tooltip content with exercise information
        const tooltipText = exercises.length > 0 
          ? formatExerciseTooltip(exercisesForMuscle)
          : muscleName; // Fallback to muscle name if no exercises provided
        
        setTooltipVisible(true);
        setTooltipContent(tooltipText);
      }
    }
  };

  const handleMouseMove = (event) => {
    if (!showTooltips) return;
    setTooltipPosition({ x: event.clientX + 10, y: event.clientY + 10 });
  };

  const handleMouseOut = (event) => {
    if (!showTooltips) return;
    
    const target = event.target;
    // Check if the target is a path or g element with an ID (muscle group)
    if (target.tagName === 'path' || target.tagName === 'g') {
      const muscleId = target.id;
      if (muscleId && muscleId !== 'full_body_back_outliner' && muscleId !== 'full_body_front_outliner') {
        setTooltipVisible(false);
      }
    }
  };

  useEffect(() => {
    highlightMuscles();
  }, [primaryMuscles, secondaryMuscles]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showTooltips) return;
    
    const allMuscles = document.querySelectorAll('.muscle-group');
    allMuscles.forEach(muscle => {
      muscle.addEventListener('mouseover', handleMouseOver);
      muscle.addEventListener('mousemove', handleMouseMove);
      muscle.addEventListener('mouseout', handleMouseOut);
    });

    return () => {
      allMuscles.forEach(muscle => {
        muscle.removeEventListener('mouseover', handleMouseOver);
        muscle.removeEventListener('mousemove', handleMouseMove);
        muscle.removeEventListener('mouseout', handleMouseOut);
      });
    };
  }, [showTooltips]); // eslint-disable-line react-hooks/exhaustive-deps

  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return { maxWidth: '300px', padding: '10px' };
      case 'large':
        return { maxWidth: '1200px', padding: '30px' };
      default:
        return { maxWidth: '600px', padding: '20px' };
    }
  };

  return (
    <Box position="relative">
      {showTooltips && (
        <Tooltip
          ref={tooltipRef}
          sx={{
            display: tooltipVisible ? 'block' : 'none',
            left: tooltipPosition.x,
            top: tooltipPosition.y
          }}
        >
          {tooltipContent}
        </Tooltip>
      )}

      <Box display="flex" justifyContent="center">
        <Box
          component={MuscleGroupSvg}
          sx={{
            width: '100%',
            maxWidth: getSizeStyles().maxWidth,
            height: 'auto',
            cursor: 'pointer',
            margin: '0 auto',
            display: 'block',
            '& path, & g': {
              transition: 'all 0.3s ease',
            },
            '& .muscle-group:hover': {
              filter: 'brightness(1.1)',
              strokeWidth: '2px',
            },
            // Dynamic highlighting styles with aesthetic colors
            [`& ${primaryMuscles.map(muscle => `#${muscle}, #${muscle} path, #${muscle} > *`).join(', ')}`]: {
              fill: '#6366f1 !important',
              stroke: '#4f46e5 !important',
              strokeWidth: '3px !important',
              filter: 'drop-shadow(0 0 6px rgba(99, 102, 241, 0.4)) !important'
            },
            [`& ${secondaryMuscles.map(muscle => `#${muscle}, #${muscle} path, #${muscle} > *`).join(', ')}`]: {
              fill: '#f59e0b !important',
              stroke: '#d97706 !important',
              strokeWidth: '2px !important',
              filter: 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.3)) !important'
            }
          }}
          onMouseOver={handleMouseOver}
          onMouseMove={handleMouseMove}
          onMouseOut={handleMouseOut}
        />
      </Box>
    </Box>
  );
};

export default MuscleHighlighter;